import { qaPanel } from '../lib/productProfile'
import * as React from 'react'
import { Link, useRouterState } from '@tanstack/react-router'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb'

type BreadcrumbTarget =
  | '/jobs'
  | '/building-review'
  | '/catalog'
  | '/asset-hub'
  | '/organizations'
  | '/admin/machines'

export type AppBreadcrumb = Readonly<{
  label: string
  to?: BreadcrumbTarget
  onClick?: () => void
  control?: React.ReactNode
}>

type DynamicBreadcrumbs = readonly AppBreadcrumb[] | undefined

const DynamicBreadcrumbsContext = React.createContext<
  | {
      breadcrumbs: DynamicBreadcrumbs
      setBreadcrumbs: React.Dispatch<React.SetStateAction<DynamicBreadcrumbs>>
    }
  | undefined
>(undefined)

const STATIC_BREADCRUMBS: Readonly<Record<string, readonly AppBreadcrumb[]>> = {
  '/production': [{ label: 'Archigma' }, { label: 'Produkcja' }],
  '/jobs': [{ label: 'Render platform' }, { label: 'Jobs' }],
  '/surroundings': [{ label: 'Archigma' }, { label: 'Surroundings' }],
  '/catalog': [{ label: 'Render platform', to: '/jobs' }, { label: 'Catalog' }],
  '/building-review': [
    {
      label: qaPanel ? 'Archigma' : 'Render platform',
      to: qaPanel ? '/building-review' : '/jobs',
    },
    { label: 'Modele budynków' },
  ],
  '/asset-hub': [
    { label: 'Render platform', to: '/jobs' },
    { label: 'Asset Hub' },
  ],
  '/settings/api-keys': [{ label: 'Account' }, { label: 'Settings' }],
  '/organizations': [{ label: 'Organization' }, { label: 'Settings' }],
  '/admin/machines': [
    { label: 'System administration' },
    { label: 'Machines' },
  ],
  '/admin/operations': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'CI/CD & DR' },
  ],
  '/admin/code-analysis': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'Code Analysis' },
  ],
  '/admin/tests': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'Tests' },
  ],
  '/admin/users': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'Users' },
  ],
  '/admin/organizations': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'Organizacje' },
  ],
  '/admin/versions': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'Versions' },
  ],
  '/admin/jobs': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'Zadania w tle' },
  ],
  '/admin/external-api-keys': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'External API keys' },
  ],
  '/admin/scheduler-settings': [
    { label: 'System administration', to: '/admin/machines' },
    { label: 'Scheduler' },
  ],
}

export function breadcrumbsForPath(pathname: string): readonly AppBreadcrumb[] {
  const normalized =
    pathname.length > 1 ? pathname.replace(/\/$/, '') : pathname
  const exact = STATIC_BREADCRUMBS[normalized]
  if (exact) return exact

  if (normalized.startsWith('/catalog/versions/')) {
    return [{ label: 'Catalog', to: '/catalog' }, { label: 'Render version' }]
  }
  if (normalized.startsWith('/asset-hub/assets/')) {
    return [{ label: 'Asset Hub', to: '/asset-hub' }, { label: 'Asset' }]
  }
  if (
    normalized.startsWith('/organizations/') &&
    normalized.endsWith('/members')
  ) {
    return [
      { label: 'Organizations', to: '/organizations' },
      { label: 'Members' },
    ]
  }
  if (
    normalized.startsWith('/organizations/') &&
    normalized.endsWith('/plugin-access')
  ) {
    return [
      { label: 'Organizations', to: '/organizations' },
      { label: 'Plugin Access' },
    ]
  }

  return [{ label: 'Render platform' }, { label: fallbackLabel(normalized) }]
}

function fallbackLabel(pathname: string) {
  const segment = pathname.split('/').filter(Boolean).at(-1) ?? 'Overview'
  return segment.replaceAll('-', ' ')
}

export function AppBreadcrumbsProvider({
  children,
}: {
  children: React.ReactNode
}) {
  const [breadcrumbs, setBreadcrumbs] =
    React.useState<DynamicBreadcrumbs>(undefined)
  const value = React.useMemo(
    () => ({ breadcrumbs, setBreadcrumbs }),
    [breadcrumbs],
  )
  return (
    <DynamicBreadcrumbsContext.Provider value={value}>
      {children}
    </DynamicBreadcrumbsContext.Provider>
  )
}

export function useAppBreadcrumbs(breadcrumbs: readonly AppBreadcrumb[]) {
  const context = React.useContext(DynamicBreadcrumbsContext)
  if (!context) {
    throw new Error('useAppBreadcrumbs requires AppBreadcrumbsProvider')
  }
  const setBreadcrumbs = context.setBreadcrumbs

  React.useEffect(() => {
    setBreadcrumbs(breadcrumbs)
    return () => setBreadcrumbs(undefined)
  }, [breadcrumbs, setBreadcrumbs])
}

export function AppBreadcrumbs() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  })
  const dynamicBreadcrumbs = React.useContext(DynamicBreadcrumbsContext)
  const breadcrumbs =
    dynamicBreadcrumbs?.breadcrumbs ?? breadcrumbsForPath(pathname)

  return (
    <Breadcrumb className="min-w-0">
      <BreadcrumbList className="flex-nowrap font-mono text-[0.68rem] font-medium uppercase tracking-[0.12em]">
        {breadcrumbs.map((breadcrumb, index) => {
          const isCurrent = index === breadcrumbs.length - 1
          return (
            <React.Fragment key={`${breadcrumb.label}-${index}`}>
              {index > 0 && (
                <BreadcrumbSeparator className="text-muted-foreground/70">
                  /
                </BreadcrumbSeparator>
              )}
              <BreadcrumbItem className="min-w-0">
                {breadcrumb.control ??
                  (isCurrent ? (
                    <BreadcrumbPage className="truncate font-mono font-medium">
                      {breadcrumb.label}
                    </BreadcrumbPage>
                  ) : breadcrumb.to ? (
                    <BreadcrumbLink
                      render={<Link to={breadcrumb.to} />}
                      className="truncate"
                    >
                      {breadcrumb.label}
                    </BreadcrumbLink>
                  ) : breadcrumb.onClick ? (
                    <BreadcrumbLink
                      render={
                        <button type="button" onClick={breadcrumb.onClick} />
                      }
                      className="truncate"
                    >
                      {breadcrumb.label}
                    </BreadcrumbLink>
                  ) : (
                    <span className="truncate text-muted-foreground">
                      {breadcrumb.label}
                    </span>
                  ))}
              </BreadcrumbItem>
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
